import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  getOptionalLeoUuid,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { Respondent, RespondentDTO } from "./respondent";
/* eslint-disable import/export */
export abstract class EditRespondentDetailsRPC implements
  LeoRPC<EditRespondentDetailsRPC.Request, EditRespondentDetailsRPC.Response, EditRespondentDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: EditRespondentDetailsRPC.Request
  ): Promise<LeoRPCResult<EditRespondentDetailsRPC.Response, EditRespondentDetailsRPC.Errors.Errors>>;
}

export namespace EditRespondentDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondent: RespondentDTO;
    spocIds: string[];
    surveyId?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isEditRespondentDetailsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondent: Respondent;
    readonly spocIds: LeoUUID[];
    readonly surveyId: LeoUUID | null;
  
  
    constructor(
    projectId: LeoUUID, 
    respondent: Respondent, 
    spocIds: LeoUUID[], 
    surveyId: LeoUUID | null = null
    ) {
      this.projectId = projectId;
      this.respondent = respondent;
      this.spocIds = spocIds;
      this.surveyId = surveyId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondent = Respondent.fromDTO(getObject(dto, "respondent", InvalidRequestRespondentError));
      const spocIds = getList<string, LeoUUID>(
        dto,
        "spocIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestSpocIdsError
      );
      const surveyId = getOptionalLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      return new Request(
        projectId,
        respondent,
        spocIds,
        surveyId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondent: this.respondent.toDTO(),
          spocIds: this.spocIds.map((e: LeoUUID) => { return e.toDTO(); }),
      };
      if (this.surveyId !== undefined && this.surveyId !== null){
        dto.surveyId = this.surveyId.toDTO();
      }
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `EditRespondentDetailsRPC(projectId='${this.projectId}',respondent='${this.respondent}',spocIds='${this.spocIds}',surveyId='${this.surveyId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `EditRespondentDetailsRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidRespondentId = "INVALID_RESPONDENT_ID",
    ProjectArchived = "PROJECT_ARCHIVED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED",
    NoSurveyQuestionsPresent = "NO_SURVEY_QUESTIONS_PRESENT",
    RespondentAssignmentInProgress = "RESPONDENT_ASSIGNMENT_IN_PROGRESS",
    MaximumRespondentSpocLimitReached = "MAXIMUM_RESPONDENT_SPOC_LIMIT_REACHED",
    InvalidSpocId = "INVALID_SPOC_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidRespondentIdDTO {
      code: string;
    }
    
    export class InvalidRespondentId extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentId;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdDTO {
        const dto: InvalidRespondentIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentId.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.INVALID_RESPONDENT_ID()`;
    };
    
    
    export interface ProjectArchivedDTO {
      code: string;
    }
    
    export class ProjectArchived extends LeoRPCError<RPCError> {
      readonly isProjectArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectArchived;
        super(code);
      }
    
    
      toDTO(): ProjectArchivedDTO {
        const dto: ProjectArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectArchived.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.PROJECT_ARCHIVED()`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
      }
    
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.SURVEY_ALREADY_SUBMITTED()`;
    };
    
    
    export interface NoSurveyQuestionsPresentDTO {
      code: string;
    }
    
    export class NoSurveyQuestionsPresent extends LeoRPCError<RPCError> {
      readonly isNoSurveyQuestionsPresent: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.NoSurveyQuestionsPresent;
        super(code);
      }
    
    
      toDTO(): NoSurveyQuestionsPresentDTO {
        const dto: NoSurveyQuestionsPresentDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    NoSurveyQuestionsPresent.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.NO_SURVEY_QUESTIONS_PRESENT()`;
    };
    
    
    export interface RespondentAssignmentInProgressDTO {
      code: string;
    }
    
    export class RespondentAssignmentInProgress extends LeoRPCError<RPCError> {
      readonly isRespondentAssignmentInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentAssignmentInProgress;
        super(code);
      }
    
    
      toDTO(): RespondentAssignmentInProgressDTO {
        const dto: RespondentAssignmentInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentAssignmentInProgress.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.RESPONDENT_ASSIGNMENT_IN_PROGRESS()`;
    };
    
    
    export interface MaximumRespondentSpocLimitReachedDTO {
      code: string;
    }
    
    export class MaximumRespondentSpocLimitReached extends LeoRPCError<RPCError> {
      readonly isMaximumRespondentSpocLimitReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumRespondentSpocLimitReached;
        super(code);
      }
    
    
      toDTO(): MaximumRespondentSpocLimitReachedDTO {
        const dto: MaximumRespondentSpocLimitReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumRespondentSpocLimitReached.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.MAXIMUM_RESPONDENT_SPOC_LIMIT_REACHED()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `EditRespondentDetailsRPC.INVALID_SPOC_ID()`;
    };
    

    export type Errors = InvalidProjectId | SurveyIsClosed | InvalidSurveyId | InvalidRespondentId | ProjectArchived | SurveyAlreadySubmitted | NoSurveyQuestionsPresent | RespondentAssignmentInProgress | MaximumRespondentSpocLimitReached | InvalidSpocId;
    export type ErrorsDTO = InvalidProjectIdDTO | SurveyIsClosedDTO | InvalidSurveyIdDTO | InvalidRespondentIdDTO | ProjectArchivedDTO | SurveyAlreadySubmittedDTO | NoSurveyQuestionsPresentDTO | RespondentAssignmentInProgressDTO | MaximumRespondentSpocLimitReachedDTO | InvalidSpocIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidRespondentId:
          return new InvalidRespondentId();
        case RPCError.ProjectArchived:
          return new ProjectArchived();
        case RPCError.SurveyAlreadySubmitted:
          return new SurveyAlreadySubmitted();
        case RPCError.NoSurveyQuestionsPresent:
          return new NoSurveyQuestionsPresent();
        case RPCError.RespondentAssignmentInProgress:
          return new RespondentAssignmentInProgress();
        case RPCError.MaximumRespondentSpocLimitReached:
          return new MaximumRespondentSpocLimitReached();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends EditRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentError extends EditRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentError";
  }
}

export class InvalidRequestSpocIdsError extends EditRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdsError";
  }
}

export class InvalidRequestSurveyIdError extends EditRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidRespondentIdError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdError";
  }
}

export class InvalidResponseProjectArchivedError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectArchivedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseNoSurveyQuestionsPresentError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNoSurveyQuestionsPresentError";
  }
}

export class InvalidResponseRespondentAssignmentInProgressError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentAssignmentInProgressError";
  }
}

export class InvalidResponseMaximumRespondentSpocLimitReachedError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumRespondentSpocLimitReachedError";
  }
}

export class InvalidResponseInvalidSpocIdError extends EditRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

